import { Head } from "@inertiajs/react"
import { AdminShell } from "@/components/AdminShell"
import { PageHeader } from "@/components/PageHeader"
import { FlashNotice } from "@/components/admin/FlashNotice"
import {
  ConnectionPanel,
  type ConnectionDetail,
} from "@/components/admin/ConnectionPanel"

type Props = {
  services: {
    roster: { configured: boolean; details: ConnectionDetail[] }
    sms: { configured: boolean; details: ConnectionDetail[]; dry_run: boolean }
    email: { configured: boolean; details: ConnectionDetail[] }
  }
}

const DESCRIPTION =
  "Check that the member roster, the text gateway and the mail relay are reachable from the Portal."

export default function AdminConnections({ services }: Props) {
  return (
    <>
      <Head title="Connections">
        <meta name="description" content={DESCRIPTION} />
        <meta property="og:title" content="Connections" />
        <meta property="og:description" content={DESCRIPTION} />
      </Head>
      <AdminShell>
        <PageHeader
          title="Connections"
          description="The Portal depends on three services it does not control. Each test below really exercises one of them, rather than reporting what the configuration says."
        />

        <div className="mt-6 space-y-6">
          <FlashNotice />

          <ConnectionPanel
            title="Member roster — SQL Server"
            description="Reads the member roster and the organization hierarchy over a read-only connection. Testing it reads two row counts and the member view's columns, and changes nothing."
            path="/admin/connections/roster"
            details={services.roster.details}
            configured={services.roster.configured}
            notConfiguredMessage="No SQL Server credentials are set for this environment, so there is nothing to test."
          />

          <ConnectionPanel
            title="Text messages — Unite"
            description="The gateway every automation sends through. Testing it acquires a token, then sends one real text to the number you type."
            path="/admin/connections/sms"
            details={services.sms.details}
            configured={services.sms.configured}
            notConfiguredMessage="Unite is not configured for this environment, so there is nothing to test."
            target={{
              label: "Send a test text to",
              placeholder: "(301) 555-0101",
              type: "tel",
              help: "Typed each time. The number is never stored and never remembered between tests.",
              buttonLabel: "Send a test text",
            }}
            warning={
              <div className="callout callout-danger">
                <p className="m-0 text-sm">
                  <strong>This sends a real text.</strong> It reaches a real handset and it
                  costs a message.
                  {services.sms.dry_run && (
                    <>
                      {" "}
                      Note that automations are currently in <strong>Rehearsal</strong>, so
                      no member is being texted — but this test deliberately bypasses that
                      switch, because a connection test that quietly rehearsed would report
                      a success that proves nothing.
                    </>
                  )}
                </p>
              </div>
            }
          />

          <ConnectionPanel
            title="Email — Google Workspace relay"
            description="Carries the delivery summary after every run. Testing it sends one real email to the address you type."
            path="/admin/connections/email"
            details={services.email.details}
            configured={services.email.configured}
            notConfiguredMessage="The mail relay is not configured for this environment, so there is nothing to test."
            target={{
              label: "Send a test email to",
              placeholder: "you@iupat.org",
              type: "email",
              help: "Typed each time. The address is never stored and never remembered between tests.",
              buttonLabel: "Send a test email",
            }}
            warning={
              <div className="callout">
                <p className="m-0 text-sm">
                  There is no mail password to get wrong: the relay authorizes the Portal
                  by IP address. If this fails on the allowlist, the fix is in Google
                  Workspace rather than in the Portal.
                </p>
              </div>
            }
          />
        </div>
      </AdminShell>
    </>
  )
}
